import { useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  DotsSixVertical,
  Image as ImageIcon,
  Link as LinkIcon,
  Play,
  Plus,
  Sparkle,
  UploadSimple,
  User,
  X,
} from '@phosphor-icons/react';
import { CAPTION_MAX, HASHTAGS_MAX, POST_PLATFORMS, type BriefAsset } from '@mybusinessmate/schema';
import { assetUrlQuery } from '../../lib/asset-url';
import { checkDeliverableFile, uploadDeliverableFile } from '../../lib/asset-upload';
import { cx } from '../../lib/cx';
import type { SectionValue, SubmitErrors, SubmitValues } from '../../lib/submit-form';
import type { Platform } from '../../lib/task-form';
import { Icon } from '../Icon';
import { Checkbox } from '../ui/checkbox';
import { DateField, TimeField } from '../ui/date-field';
import { Input } from '../ui/input';
import { SegmentedControl } from '../ui/segmented-control';
import { TagInput } from '../ui/tag-input';
import { TextArea } from '../ui/text-area';
import { useToast } from '../ui/toast';
import { AssetImage } from './asset-image';
import { toasts } from '../../lib/toast-presets';

export interface FieldsProps {
  values: SubmitValues;
  onChange: (patch: Partial<SubmitValues>) => void;
  errors: SubmitErrors;
  businessId: string;
  taskId: string;
}

const PLATFORM_LABEL: Record<Platform, string> = { instagram: 'Instagram', facebook: 'Facebook page' };
const SSL_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Not Started', label: 'None' },
] as const;

const fieldError = (errors: SubmitErrors, name: string) => errors[`deliverable.${name}`];

/** The two-line title of a card: its name, and one line saying what goes in it (Figma A05.x submit). */
export function CardHeading({ title, description, aside }: { title: string; description?: string; aside?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-(--mbm-space-3)">
      <div className="flex flex-col gap-(--mbm-space-0-5)">
        <h2 className="text-heading-h3 text-text-primary">{title}</h2>
        {description ? <p className="text-body-small text-text-secondary">{description}</p> : null}
      </div>
      {aside}
    </div>
  );
}

function Label({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-(--mbm-space-3)">
      <span className="text-label-medium text-text-primary">{children}</span>
      {aside}
    </div>
  );
}

const Counter = ({ used, max }: { used: number; max: number }) => (
  <span className="text-body-xsmall text-text-tertiary">
    {used.toLocaleString('en-IN')} / {max.toLocaleString('en-IN')}
  </span>
);

function PlatformPicker({ value, onChange }: { value: Platform[]; onChange: (next: Platform[]) => void }) {
  return (
    <fieldset className="flex flex-col gap-(--mbm-space-2)">
      <legend className="mb-(--mbm-space-2) text-label-medium text-text-primary">Post to</legend>
      <div className="flex flex-wrap gap-x-(--mbm-space-5) gap-y-(--mbm-space-2)">
        {POST_PLATFORMS.map((platform) => (
          <Checkbox
            key={platform}
            label={PLATFORM_LABEL[platform]}
            checked={value.includes(platform)}
            onChange={(e) => onChange(e.target.checked ? [...value, platform] : value.filter((p) => p !== platform))}
          />
        ))}
      </div>
    </fieldset>
  );
}

/** When it goes out and with which sound: the same three fields as the brief, so the lead can compare. */
function PublishRow({ values, onChange, errors }: Pick<FieldsProps, 'values' | 'onChange' | 'errors'>) {
  return (
    <div className="grid gap-(--mbm-space-3) sm:grid-cols-3">
      <DateField
        label="Posts on"
        value={values.publishDate}
        error={fieldError(errors, 'scheduledUploadAt')}
        onChange={(publishDate) => onChange({ publishDate })}
      />
      <TimeField
        label="Time"
        value={values.publishTime}
        disabled={!values.publishDate}
        onChange={(publishTime) => onChange({ publishTime })}
      />
      <Input
        label="Audio"
        variant="filled"
        density="compact"
        leadingIcon={Sparkle}
        placeholder="e.g. Garba Beats"
        value={values.audioNote}
        onChange={(e) => onChange({ audioNote: e.target.value })}
      />
    </div>
  );
}

function CaptionFields({
  values,
  onChange,
  errors,
  hashtags = true,
}: Pick<FieldsProps, 'values' | 'onChange' | 'errors'> & { hashtags?: boolean }) {
  return (
    <>
      <TextArea
        label="Caption"
        variant="filled"
        density="compact"
        value={values.caption}
        labelAside={<Counter used={values.caption.length} max={CAPTION_MAX} />}
        error={fieldError(errors, 'caption')}
        onChange={(e) => onChange({ caption: e.target.value })}
      />
      {hashtags ? (
        <div className="flex flex-col gap-(--mbm-space-2)">
          <Label
            aside={
              <span className="text-body-xsmall text-text-tertiary">
                {values.hashtags.length} of {HASHTAGS_MAX}
              </span>
            }
          >
            Hashtags
          </Label>
          <TagInput
            variant="filled"
            value={values.hashtags}
            onChange={(hashtags) => onChange({ hashtags })}
            placeholder="Type a hashtag and press Enter"
          />
          {fieldError(errors, 'hashtags') ? (
            <p className="text-body-xsmall text-feedback-error">{fieldError(errors, 'hashtags')}</p>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/** A post's words and plan: the slides themselves are the card above (the version's files). */
export function PostFields(props: FieldsProps) {
  return (
    <div className="flex flex-col gap-(--mbm-space-5)">
      <PlatformPicker value={props.values.platforms} onChange={(platforms) => props.onChange({ platforms })} />
      <CaptionFields {...props} />
      <PublishRow {...props} />
    </div>
  );
}

/**
 * The reel's own picture: the first video file, played from a signed link, with its length once the file says it
 * (Figma "Final reel": a portrait tile on the left of the fields).
 */
function ReelPreview({ video }: { video?: BriefAsset }) {
  const { data: url } = useQuery({ ...assetUrlQuery(video?.key ?? ''), enabled: !!video });
  const [seconds, setSeconds] = useState<number>();
  const length =
    seconds === undefined ? null : `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;
  return (
    <div className="relative aspect-[9/16] w-full overflow-hidden rounded-(--mbm-radius-md) bg-action-primary">
      {url ? (
        <video
          src={url}
          muted
          playsInline
          controls
          preload="metadata"
          onLoadedMetadata={(e) => setSeconds(e.currentTarget.duration)}
          className="size-full object-cover"
        />
      ) : (
        <span className="flex size-full items-center justify-center text-text-on-dark">
          <Icon icon={Play} size="xl" />
        </span>
      )}
      {length ? (
        <span className="pointer-events-none absolute left-(--mbm-space-2) top-(--mbm-space-2) rounded-(--mbm-radius-xs) bg-overlay-scrim px-(--mbm-space-1-5) text-label-small text-text-on-dark">
          {length}
        </span>
      ) : null}
    </div>
  );
}

/** A reel: the video beside its fields (a file, or a link if it is big), the caption, and when it posts. */
export function ReelFields({ video, uploader, ...props }: FieldsProps & { video?: BriefAsset; uploader: ReactNode }) {
  const { values, onChange } = props;
  return (
    <div className="grid gap-(--mbm-space-5) sm:grid-cols-[minmax(0,11.25rem)_minmax(0,1fr)]">
      <ReelPreview video={video} />
      <div className="flex min-w-0 flex-col gap-(--mbm-space-5)">
        {uploader}
        <Input
          label="Or video link"
          variant="filled"
          density="compact"
          leadingIcon={LinkIcon}
          placeholder="Paste a Drive or Frame.io link"
          value={values.videoUrl}
          error={fieldError(props.errors, 'videoUrl')}
          onChange={(e) => onChange({ videoUrl: e.target.value })}
        />
        <CaptionFields {...props} hashtags={false} />
        <PublishRow {...props} />
      </div>
    </div>
  );
}

/** A website update or a general task: what was done, and the page it was done on. */
export function UpdateFields({ values, onChange, errors, custom }: FieldsProps & { custom?: boolean }) {
  return (
    <div className="flex flex-col gap-(--mbm-space-5)">
      {custom ? null : (
        <Input
          label="Page"
          variant="filled"
          density="compact"
          leadingIcon={LinkIcon}
          placeholder="e.g. paradisebiryani.com/menu"
          value={values.pageUrl}
          onChange={(e) => onChange({ pageUrl: e.target.value })}
        />
      )}
      {custom ? null : (
        <TextArea
          label="What you changed"
          variant="filled"
          density="compact"
          rows={5}
          value={values.resolvedNotes}
          error={fieldError(errors, 'resolvedNotes')}
          onChange={(e) => onChange({ resolvedNotes: e.target.value })}
        />
      )}
    </div>
  );
}

/** A colour field: a swatch, and the hex. A blank or invalid hex shows no swatch. */
function ColourField({
  label,
  value,
  onChange,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  const valid = /^#[0-9a-fA-F]{6}$/.test(value);
  return (
    <div className="flex flex-col gap-(--mbm-space-2)">
      <Label>{label}</Label>
      <div className="flex items-center gap-(--mbm-space-2-5) rounded-(--mbm-radius-sm) bg-surface-tint px-(--mbm-space-3) py-(--mbm-space-2-5)">
        <span
          aria-hidden
          className="size-(--mbm-space-5) shrink-0 rounded-full border border-border-line"
          style={valid ? { background: value } : undefined}
        />
        <input
          aria-label={label}
          value={value}
          placeholder="#7A1F1F"
          onChange={(e) => onChange(e.target.value.trim())}
          className="min-w-0 flex-1 bg-transparent text-body-medium text-text-primary outline-none placeholder:text-text-muted"
        />
      </div>
      {error ? <p className="text-body-xsmall text-feedback-error">{error}</p> : null}
    </div>
  );
}

/** Figma "Site details": where it lives, how to get in, the colours. Logins themselves go to the vault, not here. */
export function WebsiteDetailsFields({ values, onChange, errors, zip }: FieldsProps & { zip: ReactNode }) {
  const toast = useToast();
  const soon = () => toast(toasts.comingSoon('The encrypted vault arrives with the vault step.'));
  return (
    <div className="flex flex-col gap-(--mbm-space-5)">
      <div className="grid gap-(--mbm-space-4) sm:grid-cols-[minmax(0,1fr)_minmax(0,16.5rem)]">
        <Input
          label="Preview / live URL"
          variant="filled"
          density="compact"
          leadingIcon={LinkIcon}
          placeholder="staging.example.com"
          value={values.liveUrl}
          error={fieldError(errors, 'liveUrl')}
          onChange={(e) => onChange({ liveUrl: e.target.value })}
          labelAside={
            values.liveUrl ? (
              <a
                href={/^https?:\/\//.test(values.liveUrl) ? values.liveUrl : `https://${values.liveUrl}`}
                target="_blank"
                rel="noreferrer noopener"
                className="text-label-medium text-brand-primary"
              >
                Open
              </a>
            ) : null
          }
        />
        <SegmentedControl
          label="SSL"
          shape="compact"
          options={[...SSL_OPTIONS]}
          value={values.sslStatus}
          onChange={(sslStatus) => onChange({ sslStatus })}
        />
      </div>
      <div className="grid gap-(--mbm-space-4) sm:grid-cols-2">
        <Input
          label="Admin panel URL"
          variant="filled"
          density="compact"
          leadingIcon={LinkIcon}
          placeholder="example.com/wp-admin"
          value={values.adminPanelUrl}
          onChange={(e) => onChange({ adminPanelUrl: e.target.value })}
        />
        <Input
          label="Admin username"
          variant="filled"
          density="compact"
          leadingIcon={User}
          value={values.adminUsername}
          onChange={(e) => onChange({ adminUsername: e.target.value })}
          labelAside={
            // TODO(api): the vault (next step) holds the password; this opens it once it exists.
            <button type="button" onClick={soon} className="text-label-medium text-brand-primary">
              Password → vault
            </button>
          }
        />
      </div>
      {zip}
      <div className="grid gap-(--mbm-space-4) border-t border-border-line pt-(--mbm-space-5) sm:grid-cols-[1fr_1fr_1.6fr]">
        <ColourField
          label="Primary colour"
          value={values.primaryColor}
          error={fieldError(errors, 'primaryColor')}
          onChange={(primaryColor) => onChange({ primaryColor })}
        />
        <ColourField
          label="Secondary colour"
          value={values.secondaryColor}
          error={fieldError(errors, 'secondaryColor')}
          onChange={(secondaryColor) => onChange({ secondaryColor })}
        />
        <TextArea
          label="Logo used"
          variant="filled"
          density="compact"
          rows={2}
          value={values.logoNotes}
          onChange={(e) => onChange({ logoNotes: e.target.value })}
        />
      </div>
    </div>
  );
}

/**
 * One section of the site: a picture so it can be checked, its name and kind, a line about it, and a tick once it is
 * built. Dropping a screenshot on the picture uploads it straight to the task's own files.
 */
function SectionRow({
  section,
  businessId,
  taskId,
  onChange,
  onRemove,
}: {
  section: SectionValue;
  businessId: string;
  taskId: string;
  onChange: (patch: Partial<SectionValue>) => void;
  onRemove: () => void;
}) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    const problem = checkDeliverableFile(file);
    if (problem) return toast({ title: 'Can’t add that file', description: problem, tone: 'error' });
    setUploading(true);
    try {
      const key = await uploadDeliverableFile(taskId)(file, businessId, () => undefined).promise;
      const image: BriefAsset = {
        id: crypto.randomUUID(),
        name: file.name,
        key,
        size: file.size,
        contentType: file.type,
        label: section.title,
      };
      onChange({ image });
    } catch {
      toast({ title: 'Couldn’t upload it', description: file.name, tone: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <li className="flex items-center gap-(--mbm-space-3) rounded-(--mbm-radius-md) border border-border-line p-(--mbm-space-2) pr-(--mbm-space-4)">
      <span aria-hidden className="text-text-muted">
        <Icon icon={DotsSixVertical} size="md" />
      </span>
      <button
        type="button"
        aria-label={`Add a screenshot of ${section.title || 'this section'}`}
        onClick={() => input.current?.click()}
        className="relative size-(--mbm-size-section-thumb) shrink-0 overflow-hidden rounded-(--mbm-radius-sm) bg-surface-field focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
      >
        {section.image ? (
          <AssetImage asset={section.image} />
        ) : (
          <span className="flex size-full items-center justify-center text-text-tertiary">
            <Icon icon={uploading ? UploadSimple : ImageIcon} size="md" />
          </span>
        )}
      </button>
      <input
        ref={input}
        type="file"
        hidden
        accept="image/png,image/jpeg,image/webp"
        aria-label={`Screenshot file for ${section.title || 'section'}`}
        onChange={(e) => {
          void pick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-(--mbm-space-0-5)">
        <div className="flex items-center gap-(--mbm-space-2)">
          <input
            aria-label="Section name"
            value={section.title}
            placeholder="Section name"
            onChange={(e) => onChange({ title: e.target.value })}
            // Sized to what is typed, so the kind tag sits right after the name as in the design.
            style={{ width: `${Math.max(section.title.length, 12) + 1}ch` }}
            className="max-w-[60%] min-w-0 bg-transparent text-label-large text-text-primary outline-none placeholder:text-text-muted"
          />
          <input
            aria-label="Section kind"
            value={section.kind}
            placeholder="Kind"
            onChange={(e) => onChange({ kind: e.target.value })}
            style={{ width: `${Math.max(section.kind.length, 4) + 2}ch` }}
            className="min-w-0 shrink-0 rounded-(--mbm-radius-xs) bg-surface-tint px-(--mbm-space-1-5) text-label-tag text-text-secondary outline-none placeholder:text-text-muted"
          />
        </div>
        <input
          aria-label="What it holds"
          value={section.description}
          placeholder="What it holds"
          onChange={(e) => onChange({ description: e.target.value })}
          className="min-w-0 bg-transparent text-body-small text-text-secondary outline-none placeholder:text-text-muted"
        />
      </div>
      <button
        type="button"
        aria-label={`Remove ${section.title || 'section'}`}
        onClick={onRemove}
        className="text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
      >
        <Icon icon={X} size="sm" />
      </button>
      <label className="flex shrink-0 cursor-pointer items-center">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={section.built}
          aria-label={`${section.title || 'Section'} is built`}
          onChange={(e) => onChange({ built: e.target.checked })}
        />
        <span
          aria-hidden
          className={cx(
            'flex size-(--mbm-size-checkbox) items-center justify-center rounded-(--mbm-component-radius-checkbox) border-[1.5px] text-text-on-dark',
            'border-border-strong peer-checked:border-action-primary peer-checked:bg-action-primary',
            'peer-focus-visible:shadow-(--mbm-shadow-focus-ring) [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100',
          )}
        >
          <Icon icon={Check} size="sm" />
        </span>
      </label>
    </li>
  );
}

/** Figma "Homepage sections": what the brief asked for, ticked as it is built. */
export function WebsiteSectionsFields({ values, onChange, businessId, taskId }: FieldsProps) {
  const sections = values.sections;
  const set = (index: number, patch: Partial<SectionValue>) =>
    onChange({ sections: sections.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
  return (
    <ul className="flex flex-col gap-(--mbm-space-2)">
      {sections.map((section, index) => (
        <SectionRow
          key={index}
          section={section}
          businessId={businessId}
          taskId={taskId}
          onChange={(patch) => set(index, patch)}
          onRemove={() => onChange({ sections: sections.filter((_, i) => i !== index) })}
        />
      ))}
      <li>
        <button
          type="button"
          onClick={() => onChange({ sections: [...sections, { title: '', kind: '', description: '', built: false }] })}
          className="flex w-full items-center justify-center gap-(--mbm-space-2) rounded-(--mbm-radius-md) border-[1.5px] border-dashed border-border-default p-(--mbm-space-3) text-label-medium text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={Plus} size="sm" />
          Add a section
        </button>
      </li>
    </ul>
  );
}
